/**
 * Organization & Membership domain (ADR-028).
 * Separate from Competition / Player — integrates via organizationId FKs.
 */

/** @typedef {'club'|'academy'|'school'|'university'|'corporate'|'association'} OrganizationType */

/**
 * @typedef {'owner'|'admin'|'coach'|'member'} OrganizationRole
 */

/**
 * @typedef {'pending'|'approved'|'rejected'|'withdrawn'|'active'|'ended'} MembershipStatus
 * pending/approved/rejected/withdrawn = join-request lifecycle;
 * active/ended = established membership.
 */

export const ORGANIZATION_TYPES = Object.freeze({
  CLUB: "club",
  ACADEMY: "academy",
  SCHOOL: "school",
  UNIVERSITY: "university",
  CORPORATE: "corporate",
  ASSOCIATION: "association",
});

export const ORGANIZATION_TYPE_LABELS = Object.freeze({
  club: "Club",
  academy: "Academy",
  school: "School",
  university: "University",
  corporate: "Corporate",
  association: "Association",
});

/** @deprecated Prefer ORGANIZATION_TYPES — kept for ADR vocabulary aliases */
export const ORGANIZATION_KINDS = Object.freeze([
  ...Object.values(ORGANIZATION_TYPES),
  "company",
  "venue_operator",
  "independent",
]);

export const ORGANIZATION_ROLES = Object.freeze({
  OWNER: "owner",
  ADMIN: "admin",
  COACH: "coach",
  MEMBER: "member",
});

export const ORGANIZATION_ROLE_LABELS = Object.freeze({
  owner: "Owner",
  admin: "Administrator",
  coach: "Coach",
  member: "Member",
});

export const MEMBERSHIP_STATUSES = Object.freeze({
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  WITHDRAWN: "withdrawn",
  ACTIVE: "active",
  ENDED: "ended",
});

export const MEMBERSHIP_STATUS_LABELS = Object.freeze({
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
  active: "Active",
  ended: "Ended",
});

/** Legacy ROLE_KEYS — map onto ORGANIZATION_ROLES where relevant */
export const ROLE_KEYS = Object.freeze({
  PLAYER: "player",
  COACH: "coach",
  REFEREE: "referee",
  ORGANIZER: "organizer",
  CLUB_ADMIN: "club_admin",
  ASSOCIATION_ADMIN: "association_admin",
  PARENT: "parent",
  OWNER: "owner",
  ADMIN: "admin",
  MEMBER: "member",
});

export function newOrganizationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `org_${crypto.randomUUID()}`;
  }
  return `org_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function newMembershipId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `oms_${crypto.randomUUID()}`;
  }
  return `oms_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function normalizeOrganizationName(name) {
  return String(name || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export function buildOrganizationSearchKeywords(name, facets = {}) {
  const tokens = new Set();
  const push = (t) => {
    const s = String(t || "")
      .trim()
      .toLowerCase()
      .slice(0, 40);
    if (s.length >= 2) tokens.add(s);
  };
  const n = normalizeOrganizationName(name);
  if (n) {
    push(n);
    n.split(/[\s._-]+/).forEach(push);
  }
  [facets.type, facets.city, facets.state, facets.country].filter(Boolean).forEach(push);
  return [...tokens].slice(0, 12);
}

/**
 * @param {Partial<object> & { organizationId: string, name: string, createdBy: string }} input
 */
export function createOrganizationShell(input) {
  const name = String(input.name || "").trim();
  const type = input.type || ORGANIZATION_TYPES.CLUB;
  const now = Date.now();
  const createdBy = input.createdBy;
  return {
    organizationId: input.organizationId || newOrganizationId(),
    name,
    normalizedName: normalizeOrganizationName(name),
    searchKeywords: buildOrganizationSearchKeywords(name, {
      type,
      city: input.city,
      state: input.state,
      country: input.country,
    }),
    type,
    kind: type,
    logoUrl: input.logoUrl || null,
    description: input.description || "",
    website: input.website || "",
    country: input.country || "",
    state: input.state || "",
    city: input.city || "",
    address: input.address || "",
    email: input.email || "",
    phone: input.phone || "",
    foundedYear: input.foundedYear ?? null,
    createdBy,
    ownerIds: input.ownerIds || [createdBy].filter(Boolean),
    adminUids: input.adminUids || [],
    isActive: input.isActive !== false,
    memberCount: input.memberCount ?? 1,
    createdAtMs: input.createdAtMs ?? now,
    updatedAtMs: input.updatedAtMs ?? now,
  };
}

/**
 * @param {object} input
 */
export function createMembershipShell(input) {
  const now = Date.now();
  const role = input.role || ORGANIZATION_ROLES.MEMBER;
  return {
    membershipId: input.membershipId || newMembershipId(),
    organizationId: input.organizationId,
    userId: input.userId,
    playerId: input.playerId ?? null,
    status: input.status || MEMBERSHIP_STATUSES.PENDING,
    role,
    roleKeys: input.roleKeys || [role],
    message: input.message || "",
    createdAtMs: input.createdAtMs ?? now,
    updatedAtMs: input.updatedAtMs ?? now,
    requestedAtMs: input.requestedAtMs ?? now,
    decidedAtMs: input.decidedAtMs ?? null,
    decidedBy: input.decidedBy ?? null,
  };
}

export function isOrgStaffRole(role) {
  return role === ORGANIZATION_ROLES.OWNER || role === ORGANIZATION_ROLES.ADMIN;
}

export function canManageOrganization(org, membership, uid) {
  if (!uid) return false;
  if (org?.createdBy === uid) return true;
  if ((org?.ownerIds || []).includes(uid)) return true;
  if ((org?.adminUids || []).includes(uid)) return true;
  if (!membership || membership.status !== MEMBERSHIP_STATUSES.ACTIVE) return false;
  return isOrgStaffRole(membership.role);
}

/**
 * @param {object[]} orgs
 * @param {{ query?: string, type?: string, country?: string, state?: string, organizationIds?: Iterable<string> | null }} filters
 */
export function filterOrganizations(orgs, filters = {}) {
  const q = (filters.query || "").trim().toLowerCase();
  const type = (filters.type || "").trim().toLowerCase();
  const country = (filters.country || "").trim().toLowerCase();
  const state = (filters.state || "").trim().toLowerCase();
  const idAllow =
    filters.organizationIds != null
      ? new Set(
          [...filters.organizationIds].map((id) => String(id || "").trim()).filter(Boolean)
        )
      : null;
  return (orgs || []).filter((o) => {
    if (o.isActive === false) return false;
    if (idAllow) {
      const oid = String(o.organizationId || o.id || "").trim();
      if (!oid || !idAllow.has(oid)) return false;
    }
    if (type && (o.type || o.kind) !== type) return false;
    if (country && !(o.country || "").toLowerCase().includes(country)) return false;
    if (state && !(o.state || "").toLowerCase().includes(state)) return false;
    if (q) {
      const hay = `${o.name || ""} ${o.city || ""} ${o.description || ""}`.toLowerCase();
      const kws = o.searchKeywords || [];
      if (!hay.includes(q) && !kws.some((k) => k.includes(q) || q.includes(k))) return false;
    }
    return true;
  });
}

export function projectOrganizationCard(org) {
  return {
    organizationId: org.organizationId || org.id,
    name: org.name || "Organization",
    type: org.type || org.kind || ORGANIZATION_TYPES.CLUB,
    typeLabel: ORGANIZATION_TYPE_LABELS[org.type || org.kind] || org.type || "Organization",
    logoUrl: org.logoUrl || null,
    city: org.city || "",
    state: org.state || "",
    country: org.country || "",
    memberCount: org.memberCount ?? 0,
    description: org.description || "",
  };
}
